/**
 * RetailerEstimateEditModal
 *
 * Managers correct a customer's name, email or the project name on an
 * estimate in their retailer's view. Deliberately NO MSRP switch and NO
 * restore — those stay with Trusscore (AdminEstimateEditModal).
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Loader2 } from 'lucide-react';
import { patchRetailerEstimate, type RetailerEstimatePatch } from '../../services/retailerAdminApi';

export interface RetailerEstimateEditable {
  id: number;
  estimate_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

interface Props {
  isOpen: boolean;
  estimate: RetailerEstimateEditable | null;
  onClose: () => void;
  onSaved: (next: RetailerEstimateEditable) => void;
}

const normText = (v: string): string | null => {
  const t = v.trim();
  return t === '' ? null : t;
};

export const RetailerEstimateEditModal: React.FC<Props> = ({ isOpen, estimate, onClose, onSaved }) => {
  const { t } = useTranslation('retaileradmin');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [estimateName, setEstimateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen && estimate) {
      setFirstName(estimate.first_name ?? '');
      setLastName(estimate.last_name ?? '');
      setEmail(estimate.email ?? '');
      setEstimateName(estimate.estimate_name ?? '');
      setError(null);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, estimate]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => { requestAnimationFrame(() => setVisible(true)); });
    } else if (mounted) {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mounted]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!mounted || !estimate) return null;

  const nextFirst = normText(firstName);
  const nextLast = normText(lastName);
  const nextEmail = normText(email);
  const nextName = normText(estimateName);
  const firstDirty = nextFirst !== estimate.first_name;
  const lastDirty = nextLast !== estimate.last_name;
  const emailDirty = nextEmail !== estimate.email;
  const nameDirty = nextName !== estimate.estimate_name;
  const dirty = firstDirty || lastDirty || emailDirty || nameDirty;

  const handleSave = async () => {
    if (!dirty) { onClose(); return; }
    if (nextEmail !== null && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setError(t('estimates.edit.invalidEmail'));
      return;
    }
    setSaving(true);
    setError(null);
    const patch: RetailerEstimatePatch = {};
    if (firstDirty) patch.firstName = nextFirst;
    if (lastDirty) patch.lastName = nextLast;
    if (emailDirty) patch.email = nextEmail;
    if (nameDirty) patch.estimateName = nextName;
    try {
      const next = await patchRetailerEstimate(estimate.id, patch);
      onSaved({ id: estimate.id, first_name: next.firstName, last_name: next.lastName, email: next.email, estimate_name: next.estimateName });
      onClose();
    } catch {
      setError(t('estimates.edit.failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`modal d-block fade ${visible ? 'show' : ''}`} tabIndex={-1} style={{ transition: 'opacity 0.3s ease', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">{t('estimates.edit.title')} <span className="text-muted">#{estimate.id}</span></h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label={t('estimates.edit.cancel')} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="row g-2 mb-3">
              <div className="col-sm-6">
                <label htmlFor="ra-edit-first" className="form-label small fw-semibold mb-1">{t('estimates.edit.firstName')}</label>
                <input id="ra-edit-first" type="text" className="form-control form-control-sm" value={firstName} disabled={saving} maxLength={100} onChange={e => setFirstName(e.target.value)} />
              </div>
              <div className="col-sm-6">
                <label htmlFor="ra-edit-last" className="form-label small fw-semibold mb-1">{t('estimates.edit.lastName')}</label>
                <input id="ra-edit-last" type="text" className="form-control form-control-sm" value={lastName} disabled={saving} maxLength={100} onChange={e => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="ra-edit-email" className="form-label small fw-semibold mb-1">{t('estimates.edit.email')}</label>
              <input id="ra-edit-email" type="email" className="form-control form-control-sm" value={email} disabled={saving} maxLength={255} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="mb-0">
              <label htmlFor="ra-edit-name" className="form-label small fw-semibold mb-1">{t('estimates.edit.project')}</label>
              <input id="ra-edit-name" type="text" className="form-control form-control-sm" value={estimateName} disabled={saving} maxLength={255} onChange={e => setEstimateName(e.target.value)} />
            </div>
          </div>
          <div className="modal-footer bg-light">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>{t('estimates.edit.cancel')}</button>
            <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" onClick={handleSave} disabled={saving || !dirty}>
              {saving ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> {t('estimates.edit.saving')}</> : <><Save size={16} /> {t('estimates.edit.save')}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerEstimateEditModal;
